import React from 'react';
import { Typography, CircularProgress, Divider } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import T from 'i18n';
import './UploadFileContent.scss';

interface IManageDrawerContentProps {
  retryUrl?: string;
}

const IManageDrawerContent: React.FC<IManageDrawerContentProps> = ({ retryUrl }) => {
  const iManageStarted = useSelector((state: any) => state.case.iManageStarted);
  const iManageDocumentStatuses = useSelector((state: any) => state.case.iManageDocumentStatuses);
  const iManageFailedDocuments = useSelector((state: any) => state.case.iManageFailedDocuments ?? []);

  const failedIds = new Set<string>(iManageFailedDocuments.map((d: any) => d.documentId));

  const documents = iManageStarted?.documents ?? [];

  return (
    <>
      {documents.length === 0 && (
        <div
          style={{
            border: '2px dashed #dcdcdc',
            borderRadius: 8,
            padding: '16px 20px',
            backgroundColor: '#f9f9f9',
            textAlign: 'center',
            marginTop: 15,
          }}
        >
          <OpenInNewIcon style={{ fontSize: 24, color: '#0078d4', marginBottom: 6 }} />
          <p style={{ margin: '0 0 4px', color: '#333', fontSize: 13 }}>
            {T.translate('case.batchUpload.iManage.selectFiles')}
          </p>
          <p className="under-label" style={{ margin: '0 0 6px' }}>
            {T.translate('case.batchUpload.iManage.filesWillAppear')}
          </p>
          <p className="under-label" style={{ margin: 0 }}>
            {T.translate('case.batchUpload.iManage.popupBlocked')}{' '}
            {retryUrl ? (
              <a
                href={retryUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0078d4' }}
              >
                {T.translate('case.batchUpload.iManage.tryAgain')}
              </a>
            ) : (
              T.translate('case.batchUpload.iManage.tryAgain')
            )}
          </p>
        </div>
      )}

      {documents.length === 0 && <Divider style={{ marginTop: 16 }} />}

      <div className="upload-file-list" style={{ paddingBottom: 30, marginTop: documents.length > 0 ? 0 : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 4, marginBottom: 8 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {T.translate('case.batchUpload.iManage.filesUploading')}
          </Typography>
        </div>

        {documents.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginLeft: 4, marginTop: 4 }}
          >
            {T.translate('case.batchUpload.iManage.waiting')}
          </Typography>
        ) : (
          <ul>
            {documents.map((doc: any) => {
              const status = iManageDocumentStatuses[doc.documentId];
              const isFailed = failedIds.has(doc.documentId) || status === 'Failed';
              const isDone = !isFailed && status === 'Success';
              const displayName = doc.extension ? `${doc.name}.${doc.extension}` : doc.name;
              return (
                <li key={doc.documentId}>
                  <span title={displayName}>
                    {displayName.length > 28
                      ? `${displayName.substring(0, 12)}...${displayName.substring(
                          displayName.length - 12,
                        )}`
                      : displayName}
                  </span>
                  {isFailed ? (
                    <FontAwesomeIcon
                      icon={faTimesCircle}
                      style={{ color: '#d32f2f', flexShrink: 0 }}
                    />
                  ) : isDone ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      style={{ color: 'green', flexShrink: 0 }}
                    />
                  ) : (
                    <CircularProgress size={16} thickness={5} style={{ flexShrink: 0 }} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
};

export default IManageDrawerContent;
